import { cn } from './ui/utils'

// bench-THE-KITCHEN-SEES-ITS-WORK.CB-F.b0 (R199 b0) — the shared bilingual pair: Arabic
// first with English beside it, each in its own language and direction; English alone when
// there is no Arabic. Canon: docs/TKF-OS-consolidated.md:747 and :762.
//
// The order and the attributes are fixed and the component never reads the page's `lang`
// or the text's language, so Arabic is first in the DOM (reading order) in every page
// direction. The space between the two comes from the wrapper's `gap-`, never a physical
// margin or padding: a physical side is the gap in an LTR page and the wrong side in an RTL
// one. The English span is muted by default; `tone="inherit"` drops that colour so a control
// (Button, Badge, reason box, dark header) keeps its own ink: a muted span inside a control
// beats the control's colour (Doc #51168). It shows what it is given — callers pass `null` for a missing Arabic, never an
// English stub.

export type BilingualTone = 'muted' | 'inherit'
export type BilingualLabel = { en: string; ar?: string | null }

interface Props {
  ar?: string | null
  en: string
  className?: string
  tone?: BilingualTone
}

export function Bilingual({ ar, en, className, tone = 'muted' }: Props) {
  const hasAr = typeof ar === 'string' && ar.trim() !== ''
  return (
    <span className={cn('inline-flex flex-wrap items-baseline gap-2', className)}>
      {hasAr && (
        <span lang="ar" dir="rtl" className="font-arabic">
          {ar}
        </span>
      )}
      <span lang="en" dir="ltr" className={tone === 'inherit' ? undefined : 'text-muted-foreground'}>
        {en}
      </span>
    </span>
  )
}

// Maps a `{ en, ar }` label object (the shape of STATUS_BADGE_LABEL entries) onto Bilingual.
export function fromPair(pair: BilingualLabel, className?: string, tone?: BilingualTone) {
  return <Bilingual ar={pair.ar} en={pair.en} className={className} tone={tone} />
}
